import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  DollarSign,
  User,
  Play,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  FileText,
} from 'lucide-react'
import { getFirestore, doc, onSnapshot } from 'firebase/firestore'
import { useCase } from '../hooks/useCase'
import { functionsClient } from '../lib/functionsClient'
import { IncomeBreakdownTable } from '../components/app/incomeVerification/summary/phases/IncomeBreakdownTable'
import type { IncomeBreakdownResult } from '../types/incomeBreakdown'
import styles from './IncomeVerificationPage.module.css'

const IncomeVerificationPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const { caseData, loading: caseLoading, error: caseError } = useCase(caseId)

  // Income breakdown state
  const [breakdown, setBreakdown] = useState<IncomeBreakdownResult | null>(null)
  const [breakdownLoading, setBreakdownLoading] = useState(true)
  const [isRunning, setIsRunning] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)

  // Subscribe to income breakdown from Firestore
  useEffect(() => {
    if (!caseId) {
      setBreakdownLoading(false)
      return
    }

    const db = getFirestore()
    const breakdownRef = doc(db, 'cases', caseId, 'income_breakdown', 'main')

    const unsubscribe = onSnapshot(
      breakdownRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setBreakdown(snapshot.data() as IncomeBreakdownResult)
        } else {
          setBreakdown(null)
        }
        setBreakdownLoading(false)
      },
      (err) => {
        console.error('Failed to load income breakdown:', err)
        setBreakdownLoading(false)
      }
    )

    return () => unsubscribe()
  }, [caseId])

  // Run income verification
  const handleRunVerification = async () => {
    if (!caseId) return

    setIsRunning(true)
    setRunError(null)

    try {
      await functionsClient.startIncomeVerificationWorkflow(caseId)
      // Success - Firestore will update via webhook
    } catch (err) {
      console.error('Failed to start income verification:', err)
      setRunError(err instanceof Error ? err.message : 'Failed to start verification')
      setIsRunning(false)
    }
  }

  const handleBackToDocuments = () => {
    navigate(`/quick-check/${caseId}/documents`)
  }

  // Count classified documents
  const classifiedDocsCount = caseData?.documents?.filter(d => d.document_type).length || 0

  if (caseLoading || breakdownLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <Loader2 size={32} className={styles.spinner} />
          <span>Loading...</span>
        </div>
      </div>
    )
  }

  if (caseError || !caseData) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <AlertCircle size={32} />
          <span>{caseError?.message || 'Failed to load case'}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={handleBackToDocuments}>
          <ArrowLeft size={18} />
          Documents
        </button>
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <DollarSign size={24} />
          </div>
          <div>
            <h1 className={styles.title}>Income Verification</h1>
            <p className={styles.subtitle}>
              <User size={14} />
              {caseData.borrowerName}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {breakdown ? (
          /* Results Available - Show Breakdown Table */
          <div className={styles.resultsSection}>
            <IncomeBreakdownTable caseId={caseId!} />
          </div>
        ) : (
          /* No Results - Show Run Button */
          <div className={styles.runSection}>
            <div className={styles.runCard}>
              <div className={styles.runIcon}>
                <Sparkles size={32} />
              </div>
              <h2>Run Income Verification</h2>
              <p>
                Fenero AI will analyze your {classifiedDocsCount} classified document{classifiedDocsCount !== 1 ? 's' : ''} to 
                calculate qualifying income and generate underwriting flags.
              </p>
              
              <div className={styles.docsSummary}>
                <FileText size={16} />
                <span>{classifiedDocsCount} documents ready for analysis</span>
              </div>

              {runError && (
                <div className={styles.runError}>
                  <AlertCircle size={16} />
                  <span>{runError}</span>
                </div>
              )}

              <button
                className={styles.runBtn}
                onClick={handleRunVerification}
                disabled={isRunning || classifiedDocsCount === 0}
              >
                {isRunning ? (
                  <>
                    <Loader2 size={20} className={styles.spinner} />
                    Running Analysis...
                  </>
                ) : (
                  <>
                    <Play size={20} />
                    Run Income Verification
                  </>
                )}
              </button>

              {classifiedDocsCount === 0 && (
                <p className={styles.noDocsHint}>
                  Please scan and classify documents first before running income verification.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default IncomeVerificationPage
